import React, { FC, ReactNode, useMemo } from 'react';

import { EvPieChart, PieDatum } from '@evinced-private/ui-common';

import { MobileChartTableLink } from 'src/common/components/mobile-chart-table-link/MobileChartTableLink';
import { ISeverityCountType } from 'src/common/components/mobile-scans-impacted-by-severity-chart/MobileScansImpactedBySeverityChart';
import { SeverityCellFormatter } from 'src/common/components/severity-cell-formatter/SeverityCellFormatter';

import { prepareDataForBreakdownBySeverityPieChart } from '../../helpers/MobileChartsHelper';

import './MobileBreakdownBySeverityPieChart.scss';

interface IPieTooltipContext {
	y: number;
	label: string;
	color: string;
	count: number;
	id: string;
	sliced: boolean;
}

interface IMobileBreakdownBySeverityPieChartProps {
	severitiesCount: ISeverityCountType[];
	onPieClick?: (severity: string) => void;
}

export const MobileBreakdownBySeverityPieChart: FC<IMobileBreakdownBySeverityPieChartProps> = ({
	severitiesCount = [],
	onPieClick
}) => {
	const showMfaBreakdownBySeverityPieChart = severitiesCount.some((s) => s.scanCount > 0);

	const pieData = useMemo(
		() => prepareDataForBreakdownBySeverityPieChart(severitiesCount),
		[severitiesCount]
	);

	if (!showMfaBreakdownBySeverityPieChart) {
		return null;
	}

	const severityFieldFormatter = (cell: string, row: PieDatum): ReactNode => {
		return (
			<MobileChartTableLink
				value={cell}
				onClick={() => onPieClick(row.severity as string)}
				customLink={<SeverityCellFormatter severity={cell} />}
			/>
		);
	};

	const customTooltip = (context: IPieTooltipContext): string => {
		const { y, label, color, count } = context;
		return `<div class="chart-tooltip">
					<div class="tooltipSymbol" style="background-color:${color}"></div> 
					${label}: <b>${count} (${y}%)</b>
				</div>`;
	};

	return (
		<EvPieChart
			title="Breakdown by severity"
			data={pieData}
			className="mobile-breakdown-by-severity-pie-chart"
			height={390}
			pieMode="thick"
			withLegend={false}
			withAnimation={true}
			withTooltip={true}
			customTooltip={customTooltip}
			dataLabelHideThreshold={3}
			dataLabelsDistance={25}
			onClick={(pieData) => onPieClick(pieData.severity)}
			headerTooltipProps={{
				tooltipText: 'This chart shows the breakdown of issues by severity level as percentages.',
				tooltipTitle: 'Severity Breakdown Information',
				tooltipPlacement: 'right'
			}}
			tableProps={{
				dataType: 'Severity',
				columnsPattern: [
					{
						name: 'Severity',
						field: 'label',
						formatter: severityFieldFormatter
					},
					{
						name: 'Total Issues',
						field: 'count'
					},
					{
						name: 'Percentage',
						field: 'value',
						formatter: (value: number) => `${value}%`
					}
				]
			}}
		/>
	);
};
